<!--
  GeometryStage -- one view's photo, its scale RULER, and its zone boxes.

  Design handoff: planning/design-handoffs/handoff_2026-08-26/
    2026-08-26-zone-geometry/ (README "4 . Geometry", design/sku-stage.jsx,
    ADR 0071 decisions 1-5). Phase 3c.

  A SEPARATE COMPONENT rather than more of SkuSetup.vue because the page
  renders TWO of these (front + back) with identical behaviour, and because
  the drag maths wants its own surface to be reasoned about. The arithmetic
  itself is in `zoneGeometry.js`, which is unit-tested without a mount --
  this file is the presentation and the pointer handling.

  WHAT THIS DELIBERATELY DOES NOT RENDER
  ======================================
  * NO "scale verified" affordance and no verdict on whether a scale is
    right (ADR 0071 decision 3, citing #2791 -- a contrast check that
    hardcoded `ok: true` in two files and could not fail). The only guard
    is a human reading catalog boxes drawn at true size. A check that
    cannot go red is worse than no check; do not add one here.
  * NO per-color "reviewed" state (decision 5). The filmstrip on the page
    showing every color's real photo with its real boxes IS the review.

  Boxes on an INHERITED color are read-only, and that is load-bearing
  rather than cosmetic: editing them would silently fork the color, and
  forking must be an explicit act (decision 4).

  data-testid prefix: sku-setup-* (shared with the page, since these are
  addressed from the same scenarios).
-->
<template>
    <div class="stage" :data-testid="`sku-setup-stage-${view.code}`">
        <!-- Caption row: what this view is, and what an inch is worth. -->
        <div class="stage__caption">
            <span class="stage__view-label">{{ view.label }}</span>
            <span class="stage__dims">{{ canvasWidth }}&times;{{ canvasHeight }}</span>

            <span
                v-if="pxPerInch"
                class="stage__ppi"
                :data-testid="`sku-setup-ppi-${view.code}`"
            >
                1&Prime; = {{ formatPpi(pxPerInch) }}px<span
                    v-if="scaleInherited"
                    class="stage__ppi-src"
                > &middot; inherited</span>
            </span>
            <span
                v-else
                class="stage__chip stage__chip--warn"
                :data-testid="`sku-setup-noscale-${view.code}`"
            >no scale</span>

            <!--
              PROVISIONAL. The badge is the whole consideration Jake got in
              exchange for unblocking the photo gate, so it is a chip in
              the caption rather than a footnote under the stage.

              It renders on `provisional` alone and NOT on `!hasImage`: a
              photo arriving does not mean anyone re-checked the boxes.
            -->
            <span
                v-if="provisional"
                class="stage__chip stage__chip--provisional"
                :title="`Authored on the ${garmentShape} silhouette, not a photo of this blank. Boxes may not match the real garment.`"
                :data-testid="`sku-setup-provisional-${view.code}`"
            >provisional</span>

            <!--
              The way OUT of provisional, offered only once there is a real
              photo to check the boxes against. With no photo there is
              nothing to confirm, and a confirm button that is always
              clickable would let someone clear the warning without ever
              seeing the garment.
            -->
            <button
                v-if="provisional && hasImage && canEdit"
                type="button"
                class="stage__mini-btn stage__mini-btn--xs"
                :data-testid="`sku-setup-confirm-geometry-${view.code}`"
                @click="$emit('confirm-geometry', { viewCode: view.code })"
            >
                Boxes match the photo
            </button>

            <span class="stage__spacer" />

            <!--
              THE RULER CONTROLS (Jake, 2026-08-27: the ruler "can't be
              moved vertically, re-oriented, or hidden/unhidden -- we need
              options for those").

              NAMING. Jake, 2026-08-27: "instead of calling it a 'line'
              call it a Ruler". The rename is VOCABULARY ONLY -- the copy a
              user reads says Ruler, while the identifiers below stay
              `line*` because they are a contract shared with SkuSetup.vue,
              zoneGeometry.js and the ui scenarios. These comments follow
              the INTERFACE, so the file reads consistently rather than
              half-renamed.

              They live in the caption rather than on the ruler itself
              because the ruler is already carrying three drag targets and
              its inch field; hanging two more buttons off it is what made
              v1 read as "complicated". MOVEMENT is the third option and is
              a drag on the ruler's body, not a button -- moving something
              by dragging it is the affordance already in the user's
              hands.
            -->
            <button
                v-if="canEdit"
                type="button"
                class="stage__mini-btn stage__mini-btn--xs"
                :title="orientation === 'vertical'
                    ? 'Turn the ruler across the garment instead'
                    : 'Turn the ruler up the garment instead'"
                :data-testid="`sku-setup-scale-orient-${view.code}`"
                @click="flipOrientation"
            >{{ orientation === "vertical" ? "&#8597; Vertical" : "&#8596; Horizontal" }}</button>

            <button
                type="button"
                class="stage__mini-btn stage__mini-btn--xs"
                :title="lineVisible
                    ? 'Hide the ruler. The scale it set is kept.'
                    : 'Show the ruler again'"
                :data-testid="`sku-setup-scale-visibility-${view.code}`"
                @click="$emit('toggle-line-visibility', { viewCode: view.code })"
            >{{ lineVisible ? "Hide Ruler" : "Show Ruler" }}</button>

            <button
                type="button"
                class="stage__mini-btn"
                :disabled="!canEdit || !unplacedZones.length"
                :data-testid="`sku-setup-add-zone-${view.code}`"
                @click="menuOpen = !menuOpen"
            >
                + Zone
            </button>
        </div>

        <!--
          NO PHOTO IS NO LONGER A REFUSAL (Jake, 2026-08-26).

          Phase 3c blocked authoring here outright: the repo silhouettes
          are GENERIC shapes, so a box placed on a stand-in cap can look
          right and be wrong. That concern is real and is NOT discarded --
          Jake's ruling keeps it VISIBLE instead of enforcing it. Zones
          can be stamped, nudged and resized on the silhouette, and the
          geometry that results is marked PROVISIONAL until a human
          confirms it against a real photo.

          Trading a block for a warning is only honest if the warning can
          actually go red, which is why `provisional` is a stored fact
          about how the geometry was authored rather than a reading of
          whether a photo is present right now. See `isProvisional` on the
          page.
        -->
        <!-- The zone picker. "+ Zone" lists what the shape offers on
             this view that is not already placed, WITH its catalog
             inches -- adding is a stamp, not a draw (decision 1). -->
        <div
            v-if="menuOpen"
            class="stage__menu"
            :data-testid="`sku-setup-zone-menu-${view.code}`"
        >
            <button
                v-for="z in unplacedZones"
                :key="z.slug"
                type="button"
                class="stage__menu-row"
                :data-testid="`sku-setup-zone-add-${view.code}-${z.slug}`"
                @click="addZone(z.slug)"
            >
                <span>{{ z.label }}</span>
                <span class="stage__menu-in">{{ formatIn(z.wIn) }}&times;{{ formatIn(z.hIn) }}</span>
            </button>
        </div>

        <!--
          tabindex so arrow keys reach the stage. Nudging is the
          affordance the stamp toast promises ("nudge them into
          place"), and it is the only way to place a box precisely --
          a pointer drag cannot resolve an eighth of an inch at this
          display scale.
        -->
        <div
            ref="frame"
            class="stage__frame"
            :style="frameStyle"
            tabindex="0"
            :data-testid="`sku-setup-stage-frame-${view.code}`"
            @pointermove="onPointerMove"
            @pointerup="endDrag"
            @pointerleave="endDrag"
            @keydown="onKeydown"
            @load.capture="onPhotoLoad"
        >
            <!-- A PER-VIEW testid prefix, so a scenario can say WHICH
                 stage's garment it means. The page draws one silhouette
                 per stage and one per filmstrip cell; ui-runner selectors
                 are a bare `[data-testid=...]` with no descendant
                 combinator, so without this every one of them answers to
                 the same id. -->
            <!--
              THE GARMENT AND THE OVERLAY MUST BE THE SAME BOX. `pad="0"`
              is not styling -- it is what makes every number this stage
              stores mean something.

              GarmentSilhouette insets its art by `pad`, default `16%`, so
              a photo left at the default occupies the middle 68% of the
              frame. The overlays above it span the frame edge to edge
              (`.stage__overlay { inset: 0 }`) with
              `preserveAspectRatio="none"`, so image space maps onto the
              WHOLE frame. Those two boxes disagreeing is a silent
              corruption of the measurement: a ruler dragged across 68% of
              the frame is recorded as spanning 100% of it, `px_per_inch`
              comes out wrong by that factor, and every zone sized from it
              is wrong by the same factor. Nothing on screen shows it,
              because the 1-inch grid that exists as the human check is
              drawn in the same wrong space and so CONFIRMS the error. It
              surfaces later as a mis-sized decoration on a storefront
              preview.

              The 16% default stays right where it is FOR THE STOREFRONT,
              which is why this is set here and not changed there: in a
              product grid the inset keeps a garment off the frame edge, a
              presentation choice with no arithmetic behind it. This stage
              is a measuring instrument, and edge-to-edge is the only
              defensible canvas for one.
            -->
            <GarmentSilhouette
                class="stage__garment"
                :silhouette="garmentShape"
                :color="color"
                :view="view.code"
                :photo-url="photoUrl"
                :testid-prefix="garmentTestidPrefix"
                :bg="stageBg"
                pad="0"
            />

            <!-- 1-inch grid, so a human can sanity-read the scale
                 against a familiar garment. This is the CHECK, and it
                 is deliberately a human one. -->
            <svg
                v-if="showGrid && pxPerInch"
                class="stage__overlay"
                :viewBox="viewBox"
                preserveAspectRatio="none"
                aria-hidden="true"
            >
                <line
                    v-for="gx in gridLinesX"
                    :key="`gx${gx}`"
                    :x1="gx"
                    :y1="0"
                    :x2="gx"
                    :y2="canvasHeight"
                    class="stage__grid-line"
                />
                <line
                    v-for="gy in gridLinesY"
                    :key="`gy${gy}`"
                    :x1="0"
                    :y1="gy"
                    :x2="canvasWidth"
                    :y2="gy"
                    class="stage__grid-line"
                />
            </svg>

            <!-- Zone boxes. Ink says where the geometry came from:
                 blue authored, blue dashed inherited, amber forked
                 (decision 5 -- inheritance is SHOWN). -->
            <svg
                class="stage__overlay"
                :viewBox="viewBox"
                preserveAspectRatio="none"
                :data-testid="`sku-setup-zones-${view.code}`"
            >
                <g
                    v-for="z in drawnZones"
                    :key="z.id"
                >
                    <!--
                      THE HALO, painted FIRST so the coloured stroke lands
                      on top of it. Document order IS paint order in SVG:
                      emitted after, it would cover the line it exists to
                      make legible. It is inert (`pointer-events: none`)
                      because it sits directly over the rect the user
                      drags.
                    -->
                    <rect
                        :x="z.rect.x"
                        :y="z.rect.y"
                        :width="z.rect.w"
                        :height="z.rect.h"
                        class="stage__zone-halo"
                        :data-testid="`sku-setup-zone-halo-${view.code}-${z.slug}`"
                    />
                    <rect
                        :x="z.rect.x"
                        :y="z.rect.y"
                        :width="z.rect.w"
                        :height="z.rect.h"
                        class="stage__zone"
                        :class="zoneClass"
                        :data-testid="`sku-setup-zone-${view.code}-${z.slug}`"
                        @pointerdown.stop="startDrag('move', z.id, $event)"
                    />
                    <line
                        :x1="z.zone.cx - 10"
                        :y1="z.zone.cy"
                        :x2="z.zone.cx + 10"
                        :y2="z.zone.cy"
                        class="stage__cross-halo"
                        :data-testid="`sku-setup-cross-halo-h-${view.code}-${z.slug}`"
                    />
                    <line
                        :x1="z.zone.cx"
                        :y1="z.zone.cy - 10"
                        :x2="z.zone.cx"
                        :y2="z.zone.cy + 10"
                        class="stage__cross-halo"
                        :data-testid="`sku-setup-cross-halo-v-${view.code}-${z.slug}`"
                    />
                    <line
                        :x1="z.zone.cx - 10"
                        :y1="z.zone.cy"
                        :x2="z.zone.cx + 10"
                        :y2="z.zone.cy"
                        class="stage__cross"
                        :class="zoneClass"
                    />
                    <line
                        :x1="z.zone.cx"
                        :y1="z.zone.cy - 10"
                        :x2="z.zone.cx"
                        :y2="z.zone.cy + 10"
                        class="stage__cross"
                        :class="zoneClass"
                    />
                    <!-- Corner handle. Dragging it edits the printable
                         area IN INCHES and records a per-GARMENT
                         override -- never a per-color one. -->
                    <rect
                        v-if="canEdit && z.id === selectedId"
                        :x="z.rect.x + z.rect.w - 14"
                        :y="z.rect.y + z.rect.h - 14"
                        width="28"
                        height="28"
                        class="stage__handle"
                        :data-testid="`sku-setup-zone-handle-${view.code}-${z.slug}`"
                        @pointerdown.stop="startDrag('resize', z.id, $event)"
                    />
                </g>

                <!-- The RULER: axis-locked, red, T-barred. It is
                     the loudest thing on the stage on purpose -- it is
                     the only value nothing downstream can check.

                     HIDDEN, not cleared, when `lineVisible` is false: the
                     scale it established survives, and so do the boxes
                     drawn at it. See the prop's comment. -->
                <g
                    v-if="lineVisible"
                    :data-testid="`sku-setup-scale-line-${view.code}`"
                >
                    <line
                        :x1="drawnLine.a.x"
                        :y1="drawnLine.a.y"
                        :x2="drawnLine.b.x"
                        :y2="drawnLine.b.y"
                        class="stage__line"
                    />
                    <!--
                      The BODY handle: drag the RULER anywhere on the
                      stage, onto the dimension actually being measured
                      (ADR 0071 decision 2, "draggable on both axes"). It
                      is a fat transparent stroke over the visible one
                      because a 5px stroke is not a pointer target, and it
                      sits before the end handles so those keep the hit
                      priority they need to set the length.
                    -->
                    <line
                        :x1="drawnLine.a.x"
                        :y1="drawnLine.a.y"
                        :x2="drawnLine.b.x"
                        :y2="drawnLine.b.y"
                        class="stage__line-body"
                        :data-testid="`sku-setup-scale-body-${view.code}`"
                        @pointerdown.stop="startDrag('lineMove', null, $event)"
                    />
                    <line
                        :x1="drawnLine.a.x - tBar.dx"
                        :y1="drawnLine.a.y - tBar.dy"
                        :x2="drawnLine.a.x + tBar.dx"
                        :y2="drawnLine.a.y + tBar.dy"
                        class="stage__line-tbar"
                    />
                    <line
                        :x1="drawnLine.b.x - tBar.dx"
                        :y1="drawnLine.b.y - tBar.dy"
                        :x2="drawnLine.b.x + tBar.dx"
                        :y2="drawnLine.b.y + tBar.dy"
                        class="stage__line-tbar"
                    />
                    <circle
                        :cx="drawnLine.a.x"
                        :cy="drawnLine.a.y"
                        r="16"
                        class="stage__line-handle"
                        :data-testid="`sku-setup-scale-handle-a-${view.code}`"
                        @pointerdown.stop="startDrag('lineA', null, $event)"
                    />
                    <circle
                        :cx="drawnLine.b.x"
                        :cy="drawnLine.b.y"
                        r="16"
                        class="stage__line-handle"
                        :data-testid="`sku-setup-scale-handle-b-${view.code}`"
                        @pointerdown.stop="startDrag('lineB', null, $event)"
                    />
                </g>
            </svg>

            <!--
              The inch field. It stays ON THE RULER -- one step, no separate
              panel, which is the design's whole point and what v1 got
              wrong. What changed (Jake, 2026-08-26: "the place where I
              type a scale obscures a lot of the product") is WHERE on the
              ruler, and how much of it is showing:

                * it hangs off the ruler's TRAILING END rather than its
                  midpoint, so it clears the middle of the stage where
                  zones live;
                * it sits BELOW the ruler rather than 74px above it. The
                  ruler's home is the hem or the brim -- the bottom edge --
                  so below it is off the garment, while above it is
                  the garment;
                * the derived "= 538 px . 76.9 px/in" readout is DERIVED
                  DETAIL, not input, and is what made the pill wide enough
                  to swallow a zone box. It shows on focus only;
                * it dims and stops taking pointers while a zone is being
                  dragged, so it can never be the thing under the cursor.
            -->
            <div
                v-if="lineVisible"
                class="stage__inch-field"
                :class="{ 'stage__inch-field--muted': dragging }"
                :style="inchFieldStyle"
                :data-testid="`sku-setup-scale-field-${view.code}`"
            >
                <input
                    :value="inches"
                    class="stage__inch-input"
                    type="text"
                    inputmode="decimal"
                    :aria-label="`Ruler length in inches on ${view.label}`"
                    :data-testid="`sku-setup-scale-input-${view.code}`"
                    @focus="fieldFocused = true"
                    @blur="fieldFocused = false; onInches($event.target.value)"
                    @change="onInches($event.target.value)"
                >
                <span class="stage__inch-unit">in</span>
                <span
                    v-if="pxPerInch && fieldFocused"
                    class="stage__inch-derived"
                    :data-testid="`sku-setup-scale-derived-${view.code}`"
                >= {{ Math.round(measuredPx) }} px &middot; {{ formatPpi(pxPerInch) }} px/in</span>
            </div>
        </div>

        <!-- What is on this stage, in words. -->
        <div
            class="stage__summary"
            :data-testid="`sku-setup-zone-summary-${view.code}`"
        >
            <template v-if="drawnZones.length">
                <span
                    v-for="(z, i) in drawnZones"
                    :key="z.id"
                >{{ i ? ' · ' : '' }}{{ z.name }} {{ formatIn(z.dims.wIn) }}&times;{{ formatIn(z.dims.hIn) }}<span
                    v-if="z.dims.isOverride"
                    class="stage__resized"
                > resized</span></span>
            </template>
            <template v-else>
                No zones yet &mdash; type the ruler's length to stamp the
                standard set for this garment.
            </template>
        </div>

        <!-- Per-zone revert. Offered ONLY on a zone that IS overridden,
             and it DELETES the override rather than copying the catalog
             figure in (Jake's rule: an override with no way back is a
             one-way door, and a copy leaves it forked while looking
             reverted). -->
        <div
            v-if="canEdit && selectedOverridden"
            class="stage__revert-row"
        >
            <span class="stage__revert-note">
                {{ selectedOverridden.name }} is
                {{ formatIn(selectedOverridden.dims.wIn) }}&times;{{ formatIn(selectedOverridden.dims.hIn) }}
                on this garment, not its catalog size.
            </span>
            <button
                type="button"
                class="stage__mini-btn"
                :data-testid="`sku-setup-zone-revert-${view.code}-${selectedOverridden.slug}`"
                @click="$emit('revert-zone-size', { viewCode: view.code, slug: selectedOverridden.slug })"
            >
                Use catalog size
            </button>
        </div>
    </div>
</template>

<script>
import GarmentSilhouette from "@/components/Storefront/GarmentSilhouette.vue";
import {
    NUDGE_IN,
    pxPerInchFrom,
    lineLengthPx,
    lineOrientation,
    moveLineEnd,
    moveLineAcross,
    flipLineOrientation,
    zoneBoxPx,
    inchesFromPx,
    snapInches,
} from "./zoneGeometry";

/**
 * The stage surface behind a DARK photo, and the default. The light neutral
 * `GarmentSilhouette` already uses, so an unsampled stage and the silhouette
 * fallback look exactly as they did.
 */
const LIGHT_STAGE_BG = "#f5f8fa";

/**
 * The stage surface behind a LIGHT photo (Jake, 2026-08-31: "Since they're both
 * white, they're very hard to see ... set a darker background instead").
 *
 * A MID SLATE, DELIBERATELY NOT BLACK. This stage is a measuring instrument
 * carrying a red ruler, blue zone boxes and a 1-inch grid; a black ground would
 * win the page and fight every one of them. #40484f puts a white garment at
 * 9.30:1 -- well clear of WCAG AA's 4.5 -- while black would sit at 21. It is
 * also on the same cool-gray axis as the surface tokens, so the stage reads as
 * a darker panel rather than a different design language.
 *
 * The value is asserted through its CONTRAST ARITHMETIC in the spec rather than
 * as a literal, so a later re-tint has to stay legible to pass.
 */
const DARK_STAGE_BG = "#40484f";

/**
 * Mean 0-255 luminance at or above which a photo counts as LIGHT.
 *
 * Jake's near-white garment measures ~250 and a black hoodie on a dark backdrop
 * lands near 30, so the boundary is nowhere near either and does not need to be
 * finely tuned. 200 keeps the dark stage for the genuinely pale photos that
 * motivated it -- a mid-gray or a color photo stays on the light neutral,
 * where it already reads.
 */
const LIGHT_PHOTO_MEAN = 200;

export default {
    name: "GeometryStage",
    components: { GarmentSilhouette },
    LIGHT_STAGE_BG,
    DARK_STAGE_BG,
    LIGHT_PHOTO_MEAN,
    props: {
        view: { type: Object, required: true },
        garmentShape: { type: String, default: "generic" },
        color: { type: Object, default: () => ({ name: "", hex: "#c9d3db" }) },
        photoUrl: { type: String, default: null },
        hasImage: { type: Boolean, default: false },
        imageWidth: { type: Number, default: 1600 },
        imageHeight: { type: Number, default: 1900 },
        // Rendered width in CSS px. The handoff derives 558 from the card
        // content box so a stage can never draw past the card border.
        displayWidth: { type: Number, default: 558 },
        /**
         * The stage's height bound in CSS px, BEFORE zoom.
         *
         * Jake, 2026-08-27: "the geometry stage is too big! It needs to be
         * vertically smaller than it is." 380 puts two stages plus their
         * captions and summaries inside one screenful at the two-up width,
         * which is what makes the filmstrip below them reachable without
         * scrolling past the thing being reviewed.
         *
         * It is a BOUND, not a height: a stage narrower than its ratio
         * needs stays governed by the width and never reaches this. See
         * `frameStyle` for why the pair cannot distort the image.
         */
        maxDisplayHeight: { type: Number, default: 380 },
        /**
         * Display magnification. 1 = fit the bounds above.
         *
         * Restored from the v1 prototype (`design/ge-app.jsx:236`, a
         * 75/100/150% segmented control), which the v2 simplification
         * sweep cut alongside the right-hand panel and the ruler gutters
         * (`design/Approach.md:24-27`) rather than for a reason of its
         * own. At real image sizes -- and now under a height cap -- that
         * trade reads wrong: zoom is how detail is recovered once the
         * stage is bounded, which is why the two changes ship together.
         *
         * PURELY A DISPLAY TRANSFORM. It scales the frame's CSS bounds and
         * nothing else; no image-space coordinate, zone centre, or
         * `px_per_inch` depends on it.
         */
        zoom: { type: Number, default: 1 },
        // Zones as authored: { id, slug, name, cx, cy }. Their SIZE is not
        // here -- it is resolved from the catalog + per-garment overrides,
        // which is what keeps inches color-invariant.
        zones: { type: Array, default: () => [] },
        zoneDimensions: { type: Function, required: true },
        offeredZones: { type: Array, default: () => [] },
        line: { type: Object, required: true },
        /**
         * Is the ruler drawn?
         *
         * Jake, 2026-08-27: the ruler "can't be ... hidden/unhidden -- we
         * need options for those". It sits on top of the garment and is in
         * the way once the scale is set.
         *
         * NAMED `lineVisible`, not `rulerVisible`, on purpose: the rename
         * Jake asked for is of the WORD THE USER READS. This prop is a
         * contract with SkuSetup.vue and the specs.
         *
         * A VISIBILITY CONTROL AND NOTHING ELSE. Hiding must never clear
         * `px_per_inch` -- that would resize or drop every box on the
         * stage, a data loss disguised as a view preference. The scale is
         * a property of the measurement, not of whether the ruler is on
         * screen, which is why this is a separate flag rather than a
         * nulling of the ruler.
         */
        lineVisible: { type: Boolean, default: true },
        inches: { type: [Number, String], default: "" },
        scaleInherited: { type: Boolean, default: false },
        showGrid: { type: Boolean, default: false },
        // FALSE on an inherited color. Boxes are read-only there because
        // editing would silently fork the color (ADR 0071 decision 4).
        canEdit: { type: Boolean, default: true },
        // 'master' | 'inherit' | 'override' for the color on the stage.
        // Drives the box ink: amber means THIS COLOR is forked, which is
        // a different fact from a zone having a resized printable area.
        colorMode: { type: String, default: "master" },
        selectedId: { type: String, default: null },
        /**
         * TRUE when this view's geometry was authored on the silhouette
         * rather than a photo of the blank, and no human has confirmed it
         * against a real one since.
         *
         * Deliberately NOT `!hasImage`. Those are different facts: the
         * photo says what is on screen now, this says how the boxes came
         * to be. Deriving it from the photo would clear the warning the
         * instant an image was attached, which is precisely what Jake
         * ruled out -- a photo arriving does not mean anyone re-checked
         * the boxes.
         */
        provisional: { type: Boolean, default: false },
    },
    emits: [
        "select-zone",
        "move-zone",
        "delete-zone",
        "resize-zone",
        "add-zone",
        "set-line",
        "set-inches",
        "toggle-line-visibility",
        "revert-zone-size",
        "confirm-geometry",
    ],
    data() {
        return {
            menuOpen: false,
            drag: null,
            fieldFocused: false,
            /**
             * The decoded size of the photo currently on the stage, once the
             * browser reports it -- `{ w, h }`, or null.
             *
             * NOT PERSISTED, and deliberately so. These numbers are a property
             * of the image file, so they are re-derivable every time it loads;
             * storing them would add a column that can go stale against the
             * thing it describes, for no information we do not already have.
             * Cleared whenever the photo changes -- see the `photoUrl` watcher,
             * where the reason it must not be latched is set out.
             */
            photoNatural: null,
            /**
             * The stage surface right now -- light by default, dark once a photo has
             * been SAMPLED and found pale.
             *
             * Held as state rather than derived from the swatch hex because the fact it
             * depends on (what the photo's pixels look like) is only knowable after the
             * file loads. See `measurePhotoContrast`.
             */
            stageBg: LIGHT_STAGE_BG,
        };
    },
    watch: {
        /**
         * A NEW PHOTO INVALIDATES THE OLD SIZE IMMEDIATELY.
         *
         * The stage swaps photos in place as the user moves between colors and
         * views, and the new one reports its size only after it decodes. Left
         * latched, the previous photo's dimensions would define the coordinate
         * space for an image of possibly quite different proportions -- a
         * wrong-canvas measurement of exactly the kind this component was
         * fixed to stop, arriving through the back door.
         *
         * Clearing means the stage falls back to the shape-derived frame for
         * the moment between the swap and the load. That is the honest
         * intermediate state: it is the same frame the silhouette is authored
         * against, and it is replaced the instant the file reports.
         */
        photoUrl() {
            this.photoNatural = null;
            /*
              THE BACKGROUND VERDICT IS ABOUT ONE PHOTO, so it dies with that photo.
              Left latched, the dark stage chosen for a white tee would sit behind the
              navy one the user clicks to next -- a dark garment on a dark ground, which
              is the very problem this exists to fix, merely inverted.
            */
            this.stageBg = LIGHT_STAGE_BG;
            this.measurePhotoContrast();
        },
    },
    mounted() {
        // The first photo is already in flight by the time this runs, so its
        // verdict is measured here rather than only on a later swap.
        this.measurePhotoContrast();
    },
    computed: {
        colorName() {
            return this.color.name || "this color";
        },
        /**
         * THE COORDINATE SPACE EVERY NUMBER ON THIS STAGE IS EXPRESSED IN.
         *
         * The photo's own pixel grid when it has reported one, and the
         * shape-derived frame (`imageWidth`/`imageHeight`) otherwise.
         *
         * WHY THIS IS NOT JUST THE PROP. Those props are constants chosen by
         * garment shape -- 1600x1900 for apparel, 960x700 for headwear -- and
         * a real photo is under no obligation to match. The photo renders
         * `object-fit: contain`, so one with different proportions LETTERBOXES:
         * bars appear on two sides and the garment no longer reaches the edges
         * of the box the overlay is drawn across. Image space then covers more
         * area than the pixels do, `px_per_inch` is measured against the wrong
         * span, and every zone sized from it is wrong -- the same corruption
         * the `pad` mismatch caused, from an independent cause, and varying per
         * photo rather than by a constant factor.
         *
         * Adopting the file's real dimensions removes the letterbox entirely:
         * the frame takes the photo's aspect ratio, so `contain` has nothing
         * left to fit and the image fills the frame the overlay spans.
         *
         * A ZERO OR ABSENT SIZE IS REFUSED. A broken or still-decoding image
         * reports 0x0, and a zero-width canvas divides every pointer mapping
         * by zero and collapses the viewBox. Falling back is the safe
         * direction; adopting a fabricated canvas is not.
         */
        /**
         * The testid prefix the garment renders under, and the SAME string
         * `onPhotoLoad` matches against. One definition rather than two: a
         * literal in the handler could drift from the prop and disable the
         * canvas fix without failing anything.
         */
        garmentTestidPrefix() {
            return `sku-setup-stage-garment-${this.view.code}`;
        },
        canvasWidth() {
            return this.photoNatural ? this.photoNatural.w : this.imageWidth;
        },
        canvasHeight() {
            return this.photoNatural ? this.photoNatural.h : this.imageHeight;
        },
        /**
         * How far the canvas has moved from the space the page authored in.
         *
         * THE SEAM THIS CLOSES. `SkuSetup` seeds the ruler (`defaultLineFor`)
         * and every stamped zone centre (`suggestedCentre`) as fractions of
         * `product.img` -- the declared per-shape frame -- and it has no
         * knowledge of the photo. Once the stage adopts the photo's real pixel
         * grid those incoming numbers are in a DIFFERENT space: a ruler seeded
         * at x=320 of a 1600-wide frame means "a fifth of the way across", but
         * read literally against a 2048-wide canvas it lands at a sixth. The
         * ruler would sit somewhere the user did not put it, and an inch value
         * typed against it would calibrate the wrong span.
         *
         * Rescaling here rather than teaching the page about photos keeps the
         * coordinate space the property of the component that owns it -- the
         * page continues to speak in proportions of the frame it declared, and
         * the stage maps them onto whatever canvas is actually rendering.
         *
         * Both axes are carried separately because the two spaces can differ in
         * ASPECT, not just size; using one factor for both would squash the
         * geometry exactly as a fixed frame height would.
         */
        canvasScale() {
            if (!this.photoNatural) return { x: 1, y: 1 };
            if (!(this.imageWidth > 0) || !(this.imageHeight > 0)) {
                return { x: 1, y: 1 };
            }
            return {
                x: this.canvasWidth / this.imageWidth,
                y: this.canvasHeight / this.imageHeight,
            };
        },
        /**
         * The ruler in CANVAS space -- what is drawn and what is measured.
         *
         * `line` arrives in the page's declared frame; this is the same ruler
         * expressed where it is rendered. `px_per_inch` is derived from THIS
         * rather than from the prop, which is what keeps the physical
         * measurement identical across the swap: the ruler and the canvas are
         * scaled by the same factor, so their ratio -- the only thing inches
         * depend on -- does not move.
         */
        drawnLine() {
            const { x, y } = this.canvasScale;
            if (x === 1 && y === 1) return this.line;
            return {
                ...this.line,
                a: { x: this.line.a.x * x, y: this.line.a.y * y },
                b: { x: this.line.b.x * x, y: this.line.b.y * y },
            };
        },
        viewBox() {
            return `0 0 ${this.canvasWidth} ${this.canvasHeight}`;
        },
        /**
         * The stage is FLUID, bounded on BOTH axes, and never distorted.
         *
         * The handoff derives a fixed 558px width from its prototype's
         * card content box. Inside the SA chrome (a 220px sidebar plus the
         * 254px rail) the available width is not fixed, and a hard 558px
         * either overflows a narrow card or leaves dead space in a wide
         * one. `aspect-ratio` keeps the photo's proportions without a
         * pixel height, so the boxes stay true to the image regardless of
         * the rendered width -- the inch math is in image space and does
         * not care how big the frame is on screen. THAT REASONING STANDS
         * and the ratio below is load-bearing, not decoration.
         *
         * WHAT CHANGED (Jake, 2026-08-27): "the geometry stage is too
         * big! It needs to be vertically smaller than it is." Width alone
         * did not bound it. A 960x700 cap at the full card width renders
         * enormous, and TWO stages sit side by side, so the section ran
         * far past a screenful before a single box could be checked.
         *
         * THE FIX IS A SECOND BOUND, NOT A HEIGHT. `maxHeight` alongside
         * `aspectRatio` is what keeps this safe: under CSS aspect-ratio a
         * max-height shrinks the frame's WIDTH to match, so the stage
         * LETTERBOXES within the available space and every box on it stays
         * proportionally true. Setting `height` instead would satisfy the
         * same complaint by SQUASHING the image -- which silently makes
         * every zone on the stage wrong, this feature's cardinal sin, and
         * exactly the failure ADR 0071 exists to prevent. The paired
         * assertion in the spec ("keeps the image's aspect ratio while
         * bounding the height") is what stops a later edit making that
         * trade.
         *
         * Both bounds are multiplied by `zoom` TOGETHER, so zooming is a
         * uniform scale of one box rather than an independent stretch of
         * two edges. `zoom` deliberately does not touch image-space
         * coordinates -- see `toImage`.
         */
        frameStyle() {
            return {
                width: "100%",
                maxWidth: `${Math.round(this.displayWidth * this.zoom)}px`,
                maxHeight: `${Math.round(this.maxDisplayHeight * this.zoom)}px`,
                aspectRatio: `${this.canvasWidth} / ${this.canvasHeight}`,
            };
        },
        measuredPx() {
            return lineLengthPx(this.drawnLine.a, this.drawnLine.b);
        },
        pxPerInch() {
            return pxPerInchFrom(this.measuredPx, Number(this.inches));
        },
        /**
         * Zones with their resolved inches and derived pixel rect. A zone
         * whose slug has NO catalog row and no override resolves to null
         * and is dropped rather than drawn at an invented size -- ADR
         * 0071's "provisional geometry drawn confidently" is the failure
         * this avoids.
         */
        drawnZones() {
            if (!this.pxPerInch) return [];
            return this.zones
                .map((zone) => {
                    const dims = this.zoneDimensions(this.view.code, zone.slug);
                    if (!dims) return null;
                    // The centre arrives in the page's declared frame and is
                    // mapped onto the canvas actually being drawn -- see
                    // `canvasScale`. The SIZE is not mapped: it comes from
                    // inches via `px_per_inch`, which is already expressed in
                    // canvas pixels, so scaling it here would apply the factor
                    // twice.
                    const { x: sx, y: sy } = this.canvasScale;
                    const drawn = {
                        ...zone,
                        cx: zone.cx * sx,
                        cy: zone.cy * sy,
                    };
                    const rect = zoneBoxPx(
                        { cx: drawn.cx, cy: drawn.cy, wIn: dims.wIn, hIn: dims.hIn },
                        this.pxPerInch
                    );
                    if (!rect) return null;
                    return { ...zone, zone: drawn, dims, rect };
                })
                .filter(Boolean);
        },
        /**
         * Which ink a box gets. The handoff is specific: "blue `#119ae9`
         * where geometry is authored, blue DASHED `#4b7fa8` where it is
         * inherited, amber `#c77800` WHERE THE COLOR IS DIFFERENT".
         *
         * Note what amber keys on: the COLOR being forked, not a resized
         * zone. An earlier revision keyed it on `dims.isOverride` (the
         * per-GARMENT printable-area override), which is a different fact
         * entirely -- a forked color's boxes rendered blue, and a merely
         * resized zone on the master rendered amber. Both readings of the
         * stage were wrong, and the ink is the only thing on screen that
         * says which geometry you are about to edit (ADR 0071 decision 5).
         *
         * A resized zone is reported in words in the summary line below
         * the stage instead, which is where an inch figure belongs.
         */
        zoneClass() {
            if (this.colorMode === "override") return "stage__zone--different";
            if (!this.canEdit) return "stage__zone--inherited";
            return "stage__zone--authored";
        },
        selectedOverridden() {
            return (
                this.drawnZones.find(
                    (z) => z.id === this.selectedId && z.dims.isOverride
                ) || null
            );
        },
        unplacedZones() {
            const placed = new Set(this.zones.map((z) => z.slug));
            return this.offeredZones.filter((z) => !placed.has(z.slug));
        },
        /** A zone is under the cursor right now. */
        dragging() {
            return (
                Boolean(this.drag) &&
                !["lineA", "lineB", "lineMove"].includes(this.drag.mode)
            );
        },
        /** The axis the ruler is locked to -- 'horizontal' | 'vertical'. */
        orientation() {
            return lineOrientation(this.line);
        },
        /**
         * The T-bar caps, perpendicular to the line.
         *
         * Perpendicular is the point: a cap drawn along the ruler would
         * be invisible against it. This turns with the ruler so the
         * flipped ruler still reads as a caliper rather than a bare
         * stroke.
         */
        tBar() {
            return this.orientation === "vertical"
                ? { dx: 18, dy: 0 }
                : { dx: 0, dy: 18 };
        },
        /**
         * Where the inch field sits, in PERCENT of the frame.
         *
         *
         * Percent rather than px because the frame is fluid: a px offset
         * computed from a nominal display width drifts off the line at
         * any other rendered size.
         *
         * THE POSITION IS THE FIX (Jake, 2026-08-26). It used to hang off
         * the line's MIDPOINT, lifted a fixed 74 CSS px. Both halves were
         * wrong on a short frame. The cap is 960x700 with its line at
         * y=0.74, so at the 558px cap width the frame renders ~407px tall
         * and `midY% - 74px` landed the field ~56% down it -- horizontally
         * dead-centre, which is exactly where `cap_front_panel` stamps
         * (cx = w * 0.5). The field covered the crown and the box on it.
         *
         * Now it is anchored to the line's TRAILING END and hangs BELOW,
         * both expressed as percentages of the line's own geometry so the
         * field moves WITH the line instead of with the stage:
         *
         *   * the line's home is the hem or the brim -- the bottom edge --
         *     so downward is off the garment and upward is the garment;
         *   * anchoring to the end rather than the middle clears the
         *     zone-bearing centre of the stage entirely;
         *   * `translateX(0)` (see the stylesheet) means the pill grows
         *     outward from that end rather than straddling it.
         *
         * The 2.2% below the line is the T-bar's half-height plus a
         * little, so the pill clears the bar without floating away from
         * the line it belongs to. It stays clamped inside the frame:
         * `min()` keeps it off the bottom edge on a line drawn very low.
         */
        inchFieldStyle() {
            const endX = Math.max(this.drawnLine.a.x, this.drawnLine.b.x);
            const lineY = (this.drawnLine.a.y + this.drawnLine.b.y) / 2;
            const leftPct = (endX / this.canvasWidth) * 100;
            const topPct = (lineY / this.canvasHeight) * 100 + 2.2;
            return {
                // Nudged back inside the frame so a line ending at the
                // image edge does not push the pill out of view.
                left: `min(${leftPct.toFixed(3)}%, calc(100% - 132px))`,
                top: `min(${topPct.toFixed(3)}%, calc(100% - 34px))`,
            };
        },
        gridLinesX() {
            const out = [];
            if (!this.pxPerInch) return out;
            for (let x = this.pxPerInch; x < this.canvasWidth; x += this.pxPerInch) {
                out.push(Math.round(x));
            }
            return out;
        },
        gridLinesY() {
            const out = [];
            if (!this.pxPerInch) return out;
            for (let y = this.pxPerInch; y < this.canvasHeight; y += this.pxPerInch) {
                out.push(Math.round(y));
            }
            return out;
        },
    },
    methods: {
        formatPpi(n) {
            return (Math.round(n * 10) / 10).toFixed(1);
        },
        formatIn(n) {
            return `${Number(n.toFixed(2))}″`;
        },
        selectZone(id) {
            this.$emit("select-zone", id);
        },
        addZone(slug) {
            this.menuOpen = false;
            this.$emit("add-zone", { viewCode: this.view.code, slug });
        },
        /**
         * Flip the ruler between horizontal and vertical.
         *
         * Jake, 2026-08-27, deciding the shape of this control: the ruler
         * flips between the two AXES and stays locked in whichever it is
         * in. Diagonal remains unrepresentable -- `flipLineOrientation`
         * returns a line built by the same constructor as every other
         * mutation, so there is no state in between the two axes for a
         * flip to land in.
         *
         * ADR 0071 decision 2's guard was never about forbidding a
         * vertical measurement; it forbids a HYPOTENUSE. A garment
         * dimension that runs top-to-bottom (a cap's crown height, a
         * tote's depth) is exactly as square to the camera as one that
         * runs across, and refusing to measure it forced the user onto a
         * dimension they might not know.
         */
        flipOrientation() {
            this.$emit("set-line", {
                viewCode: this.view.code,
                line: flipLineOrientation(this.line),
            });
        },
        onInches(raw) {
            const value = Number(String(raw).trim());
            this.$emit("set-inches", {
                viewCode: this.view.code,
                inches: Number.isFinite(value) && value > 0 ? value : 0,
            });
        },
        /**
         * Pointer position in IMAGE pixel space.
         *
         * The scale is read off the LIVE rect rather than from
         * `displayWidth`, which is what lets `zoom` be a pure CSS
         * transform: a frame rendered at any size still maps a pointer to
         * the right image pixel, so no zone centre, line coordinate or
         * `px_per_inch` depends on the magnification. (The v1 prototype
         * makes the same choice for the same reason -- ge-stage.jsx:194,
         * "so an outer CSS transform cannot desynchronise pointer math".)
         *
         * A ZERO-WIDTH FRAME RETURNS null, and callers abandon the drag.
         * Dividing by a zero width yields Infinity, and an Infinity
         * coordinate propagates into the stored line -- from where it
         * reaches `px_per_inch` as NaN and silently unresolves every box
         * on the stage. A frame can genuinely measure zero: inside a
         * display:none ancestor, before first layout, or in any renderer
         * that does not lay out. Refusing to convert is the safe
         * direction; a fabricated coordinate is exactly the
         * silently-wrong-geometry failure this feature exists to avoid.
         */
        /**
         * Read the mean brightness of an image, 0-255, by drawing it small.
         *
         * A 16x16 draw is enough: the question is "is this thing overall pale",
         * not what any individual pixel is, and downscaling averages the source
         * for us in the browser's own resampler for far less work than walking
         * a 1200x1800 buffer.
         *
         * THROWS on a tainted canvas, and the caller is written for that -- see
         * `measurePhotoContrast`. Kept separate from the decision so a test can
         * substitute the failure without a real cross-origin load.
         */
        sampleMeanLuminance(img) {
            const canvas = document.createElement("canvas");
            canvas.width = 16;
            canvas.height = 16;
            const ctx = canvas.getContext("2d");
            if (!ctx) return null;
            ctx.drawImage(img, 0, 0, 16, 16);
            const { data } = ctx.getImageData(0, 0, 16, 16);
            let total = 0;
            let counted = 0;
            for (let i = 0; i < data.length; i += 4) {
                // Skip fully transparent pixels -- a PNG cut out to alpha would
                // otherwise average toward whatever its unpainted RGB happens to
                // be and report a verdict about nothing.
                if (data[i + 3] === 0) continue;
                total +=
                    (data[i] * 299 + data[i + 1] * 587 + data[i + 2] * 114) /
                    1000;
                counted += 1;
            }
            return counted ? total / counted : null;
        },

        /** Apply a measured mean luminance to the stage surface. */
        applyPhotoLuminance(mean) {
            if (!Number.isFinite(mean)) return;
            this.stageBg =
                mean >= LIGHT_PHOTO_MEAN ? DARK_STAGE_BG : LIGHT_STAGE_BG;
        },

        /**
         * Decide the stage surface from THE PHOTO'S OWN PIXELS.
         *
         * WHY NOT THE SWATCH HEX. `GarmentSilhouette.isLight` is right there and
         * would be the obvious reach, but it answers a different question. The
         * swatch describes the GARMENT while the stage has to contend with the
         * PHOTO -- mostly the studio backdrop the garment was shot on. Jake's
         * case is exactly the divergence: a photo averaging (250, 250, 252)
         * beside a swatch that can say "Red #c8102e". Keying on the hex would
         * have left the one image that motivated this on a white ground.
         *
         * IT LOADS ITS OWN IMAGE rather than reading the one on screen. The
         * displayed <img> belongs to `GarmentSilhouette`, which also renders the
         * storefront; setting `crossOrigin` on it would make every shopper-facing
         * product image FAIL TO LOAD on any bucket that does not answer with CORS
         * headers -- a blank product grid traded for a legibility nicety. A
         * private Image here means the worst case is that the background stays
         * light. (The 8087 MinIO bucket does answer with
         * `Access-Control-Allow-Origin`, verified 2026-08-31, so the read is
         * expected to succeed; the fallback covers the buckets that might not.)
         *
         * Resolves either way and never rejects -- an unreadable photo is a
         * missed nicety, not a broken stage.
         */
        measurePhotoContrast() {
            const url = this.photoUrl;
            if (!url || typeof document === "undefined") return Promise.resolve();
            return new Promise((resolve) => {
                const probe = new Image();
                probe.crossOrigin = "anonymous";
                probe.onload = () => {
                    // The photo may have changed while this one decoded; a stale
                    // verdict applied now would describe an image that is gone.
                    if (this.photoUrl !== url) return resolve();
                    try {
                        this.applyPhotoLuminance(this.sampleMeanLuminance(probe));
                    } catch {
                        // Tainted canvas (cross-origin without CORS), or no 2d
                        // context. Keep the light default and the stage renders.
                    }
                    resolve();
                };
                // A CORS-less bucket fails the request outright under
                // `crossOrigin`. That is the expected miss, not an error.
                probe.onerror = () => resolve();
                probe.src = url;
            });
        },

        /**
         * Adopt the photo's real pixel dimensions as the stage's canvas.
         *
         * ON THE FRAME WITH `.capture`, not on the component. `load` does not
         * bubble, so a listener on an ancestor hears it only in the capture
         * phase -- and a `@load` on `<GarmentSilhouette>` would not hear it at
         * all: the component declares no emits and no `inheritAttrs: false`, so
         * the listener would fall through to its root <div>, which never loads
         * anything. Capturing on the frame is also what keeps the fix inside
         * this component: `GarmentSilhouette` is shopper-facing, and it needs no
         * change to be measured against.
         *
         * IT MUST BE THE PHOTO. The fallback path renders the silhouette's SVG
         * art through the same frame, and that art's intrinsic size describes a
         * drawing, not this garment -- adopting it would define the coordinate
         * space from a stand-in shape.
         *
         * The guard is DERIVED FROM `garmentTestidPrefix`, not a literal.
         * `GarmentSilhouette` names its photo `${testidPrefix}-photo`, so a
         * hardcoded id would stop matching the moment this stage passed a
         * prefix -- and it would fail SILENTLY, leaving the canvas on its
         * declared fallback while every test that mounted the component with
         * the default prefix still passed. Sharing one source for the prop and
         * the guard is what makes that impossible rather than merely unlikely.
         *
         * A NON-POSITIVE SIZE IS IGNORED rather than stored. See `canvasWidth`
         * for why a zero canvas is worse than a wrong-but-sane one.
         */
        onPhotoLoad(event) {
            const img = event.target;
            if (
                !img ||
                img.dataset?.testid !== `${this.garmentTestidPrefix}-photo`
            ) {
                return;
            }
            const w = Number(img.naturalWidth);
            const h = Number(img.naturalHeight);
            if (!(w > 0) || !(h > 0)) return;
            this.photoNatural = { w, h };
        },

        toImage(event) {
            const frame = this.$refs.frame;
            if (!frame) return null;
            const rect = frame.getBoundingClientRect();
            if (!(rect.width > 0) || !(rect.height > 0)) return null;
            /*
              RETURNS PAGE SPACE, NOT CANVAS SPACE, and the asymmetry is
              deliberate. Everything this component EMITS -- a moved ruler end,
              a translated ruler, a zone nudge -- is stored by `SkuSetup` in the
              frame it declared, and it has no knowledge of the photo. Handing
              it a canvas-space coordinate would write the photo's grid into
              state seeded in the frame's, so the two would disagree the moment
              a different photo loaded.

              So the pointer is converted to canvas space (what is on screen)
              and then back through `canvasScale` (what the page stores in).
              Rendering reads the same mapping in the other direction, which is
              what keeps a dragged handle under the cursor.
            */
            const scale = this.canvasWidth / rect.width;
            const { x: sx, y: sy } = this.canvasScale;
            return {
                x: ((event.clientX - rect.left) * scale) / sx,
                y: ((event.clientY - rect.top) * scale) / sy,
            };
        },
        /**
         * Begin a drag, and STOP THE BROWSER STARTING ONE OF ITS OWN.
         *
         * Jake, 2026-08-27: "when manipulating the Ruler, the SVG behind
         * it often gets activated causing an annoying visual glitch."
         *
         * THE CAUSE IS A NATIVE GESTURE, NOT PAINT ORDER. The garment is
         * rendered by `GarmentSilhouette` as <img> elements, and an <img>
         * is natively draggable in every browser. A `pointerdown` that is
         * not defaultPrevented therefore lets the browser begin its own
         * image-drag and selection gesture underneath ours -- the garment
         * picks up a selection highlight and a drag ghost while the user
         * is dragging the ruler. No z-index can stop that; only
         * `preventDefault` can, which is why it is here and not a style.
         *
         * POINTER CAPTURE is the second half. Moves are handled on the
         * FRAME, so without capture a drag is tracked only while the
         * pointer stays inside it -- slip past the edge mid-drag and the
         * gesture is handed back to whatever is underneath. Capture binds
         * the whole gesture to the element that was grabbed. It is guarded
         * because jsdom and older engines do not implement it, and
         * throwing here would kill the drag outright.
         */
        startDrag(mode, id, event) {
            if (id) this.selectZone(id);
            if (!this.canEdit) return;
            const origin = this.toImage(event);
            if (!origin) return;
            if (event.cancelable) event.preventDefault();
            const target = event.currentTarget || event.target;
            if (target && typeof target.setPointerCapture === "function") {
                try {
                    target.setPointerCapture(event.pointerId);
                } catch {
                    // A stale or synthetic pointerId is not worth losing
                    // the drag over -- the frame handler still tracks it.
                }
            }
            this.drag = { mode, id, origin };
        },
        /**
         * Arrow keys nudge the selected zone; shift makes the step an inch.
         * Delete removes it. Both are emitted as STEPS -- the page owns the
         * inch-to-pixel conversion because it owns the scale.
         */
        onKeydown(event) {
            if (!this.canEdit || !this.selectedId) return;
            const deltas = {
                ArrowLeft: [-1, 0],
                ArrowRight: [1, 0],
                ArrowUp: [0, -1],
                ArrowDown: [0, 1],
            };
            if (deltas[event.key]) {
                event.preventDefault();
                const [dx, dy] = deltas[event.key];
                this.$emit("move-zone", {
                    viewCode: this.view.code,
                    id: this.selectedId,
                    dx,
                    dy,
                    coarse: event.shiftKey,
                });
                return;
            }
            if (event.key === "Delete" || event.key === "Backspace") {
                event.preventDefault();
                this.$emit("delete-zone", {
                    viewCode: this.view.code,
                    id: this.selectedId,
                });
            }
        },
        endDrag() {
            this.drag = null;
        },
        onPointerMove(event) {
            if (!this.drag || !this.canEdit) return;
            const at = this.toImage(event);
            if (!at) return;

            /*
              THE THREE RULER DRAGS. All three route through
              `zoneGeometry`'s constructors, which is what makes a
              hypotenuse UNREPRESENTABLE rather than merely rejected --
              this handler no longer decides an axis or writes a
              coordinate, so there is nothing here to get wrong. See the
              module header for why the old inline axis-sniff had to go
              (it also made a vertical line unreachable).
            */
            if (this.drag.mode === "lineA" || this.drag.mode === "lineB") {
                // Along the axis: this is what SETS the measured length.
                this.$emit("set-line", {
                    viewCode: this.view.code,
                    line: moveLineEnd(
                        this.line,
                        this.drag.mode === "lineA" ? "a" : "b",
                        at
                    ),
                });
                return;
            }

            if (this.drag.mode === "lineMove") {
                // TRANSLATE the ruler -- across the garment AND along its
                // own axis (Jake, 2026-08-27: "I should be able to move it
                // wherever I want it"). Length preserved, so `px_per_inch`
                // does not move while the user is only repositioning.
                //
                // The drag ORIGIN is passed as the grab point so the ruler
                // moves by the pointer's delta. Without it the ruler would
                // re-centre on the cursor and jump out from under the spot
                // the user grabbed. The origin is advanced to `at` so the
                // next pointermove measures from where the ruler now is.
                this.$emit("set-line", {
                    viewCode: this.view.code,
                    line: moveLineAcross(this.line, at, this.drag.origin),
                });
                this.drag.origin = at;
                return;
            }

            if (this.drag.mode === "move" && this.pxPerInch) {
                // Report the drag as NUDGE STEPS rather than raw pixels so
                // a dragged box lands on the same eighth-inch grid a
                // keyboard nudge uses -- one snap rule, not two.
                //
                // THE STEP IS CONVERTED INTO PAGE SPACE FIRST. `pxPerInch` is
                // measured on the CANVAS (the photo's grid), while `at` and the
                // drag origin are in the page's declared frame -- see
                // `toImage`. Comparing them directly would scale every nudge by
                // the canvas ratio, so a box would travel the wrong distance
                // under the cursor on any photo whose size differs from the
                // declared frame. Dividing by the same factor the coordinates
                // were divided by puts both sides in one space.
                // PER AXIS, because the two spaces can differ in ASPECT and
                // not merely in size: a 4:3 photo in a 1600x1900 frame is
                // scaled by a different factor on each axis, so one step value
                // would be right horizontally and wrong vertically.
                const stepX = (NUDGE_IN * this.pxPerInch) / this.canvasScale.x;
                const stepY = (NUDGE_IN * this.pxPerInch) / this.canvasScale.y;
                const dx = Math.round((at.x - this.drag.origin.x) / stepX);
                const dy = Math.round((at.y - this.drag.origin.y) / stepY);
                if (!dx && !dy) return;
                this.$emit("move-zone", {
                    viewCode: this.view.code,
                    id: this.drag.id,
                    dx,
                    dy,
                });
                // Advance the origin by what was actually applied, so the
                // remainder is not re-sent on the next pointermove.
                this.drag.origin = {
                    x: this.drag.origin.x + dx * stepX,
                    y: this.drag.origin.y + dy * stepY,
                };
                return;
            }

            if (this.drag.mode === "resize") {
                const zone = this.drawnZones.find((z) => z.id === this.drag.id);
                if (!zone || !this.pxPerInch) return;
                // A corner drag edits the PRINTABLE AREA IN INCHES. The
                // pixel extent is a rendering of it, so the inches are what
                // gets recorded -- and recorded per GARMENT, never per
                // color (Jake's rule).
                const wPx = Math.abs(at.x - zone.zone.cx) * 2;
                const hPx = Math.abs(at.y - zone.zone.cy) * 2;
                const wIn = inchesFromPx(wPx, this.pxPerInch);
                const hIn = inchesFromPx(hPx, this.pxPerInch);
                if (!wIn || !hIn) return;
                this.$emit("resize-zone", {
                    viewCode: this.view.code,
                    slug: zone.slug,
                    wIn: snapInches(wIn, NUDGE_IN),
                    hIn: snapInches(hIn, NUDGE_IN),
                });
            }
        },
    },
};
</script>

<style scoped>
.stage {
    min-width: 0;
}
/*
  The caption row WRAPS.

  It used to be a nowrap flex row, which was fine for three short items.
  Adding the provisional chip and its confirm button overflowed it: at the
  two-stage width the button rode straight over the neighbouring stage's
  scale readout and rendered as "Boxes matchSidethe photo". Caught by
  looking at a screenshot on 8088 -- every DOM assertion passed, because
  overflow is invisible to a presence check.

  `white-space: nowrap` stays on the ITEMS (so an individual chip or
  readout never breaks mid-phrase) while the ROW is free to wrap, which is
  the combination that keeps both stages legible in a narrow card.
*/
.stage__caption {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 8px 6px;
    margin-bottom: 8px;
    min-width: 0;
}
.stage__caption > * {
    white-space: nowrap;
}
/* The spacer only does its job on an unwrapped row; once the row wraps it
   would push the button onto a line of its own for no reason. */
.stage__caption .stage__spacer {
    flex: 1 1 0;
    min-width: 0;
}
.stage__view-label {
    font-size: 14px;
    font-weight: 500;
    color: var(--tf-fg-1);
}
.stage__dims,
.stage__ppi,
.stage__inch-derived {
    font-family: ui-monospace, "SF Mono", Menlo, Consolas, monospace;
    font-size: 11px;
    color: var(--tf-fg-3);
}
.stage__ppi-src {
    color: #4b7fa8;
}
.stage__spacer {
    flex: 1 1 auto;
}
.stage__chip {
    border-radius: 50px;
    padding: 3px 10px;
    font-size: 11px;
    font-weight: 500;
    text-transform: uppercase;
    letter-spacing: 0.4px;
}
.stage__chip--warn {
    background: #fff8e1;
    color: #b26a00;
}
/* Amber, like the other "this is not settled" ink on the stage. Not red:
   provisional geometry is usable, it is just unverified. */
.stage__chip--provisional {
    background: #fdf0dc;
    color: #8a5200;
    border: 1px solid #e2b877;
}
.stage__mini-btn {
    height: 32px;
    padding: 0 14px;
    border-radius: 50px;
    border: 1px solid var(--tf-border);
    background: var(--tf-surface);
    font-size: 13px;
    font-weight: 500;
    color: var(--tf-fg-2);
    cursor: pointer;
    transition: background 150ms cubic-bezier(0.4, 0, 0.2, 1),
        border-color 150ms cubic-bezier(0.4, 0, 0.2, 1);
}
.stage__mini-btn:hover:not(:disabled) {
    background: var(--tf-surface-alt);
}
.stage__mini-btn:disabled {
    opacity: 0.45;
    cursor: default;
}
/* Secondary to the badge it resolves: the WARNING is the thing to read,
   the button is what you do about it. */
.stage__mini-btn--xs {
    height: 26px;
    padding: 0 10px;
    font-size: 12px;
}
.stage__empty {
    border: 1px dashed var(--tf-border-dotted, #d3d3d3);
    border-radius: 8px;
    padding: 28px 20px;
    font-size: 12.5px;
    color: var(--tf-fg-3);
    text-align: center;
}
.stage__frame {
    position: relative;
    border: 1px solid var(--tf-border);
    background: var(--tf-surface);
    overflow: hidden;
    touch-action: none;
}
.stage__garment,
.stage__overlay {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
}
/*
  THE GARMENT IS SCENERY, NOT CONTENT (Jake, 2026-08-27: "the SVG behind it
  often gets activated causing an annoying visual glitch").

  `preventDefault` in `startDrag` is the real fix and stops the native
  gesture at its source. This is the second line of defence for the paths
  that never reach a handler at all -- a pointerdown that lands on the
  garment BETWEEN the ruler and a box, or a double-click, which selects the
  images and leaves them highlighted. Selection and dragging are the two
  native behaviours an <img> offers, and neither is wanted on a backdrop
  that exists to be measured against.
*/
.stage__garment {
    user-select: none;
    -webkit-user-select: none;
    -webkit-user-drag: none;
}
.stage__garment img {
    pointer-events: none;
    -webkit-user-drag: none;
}
.stage__overlay {
    pointer-events: none;
}
.stage__overlay rect,
.stage__overlay circle {
    pointer-events: auto;
}
.stage__grid-line {
    stroke: rgba(17, 154, 233, 0.16);
    stroke-width: 1.4;
}
/*
  THE PLACEMENT LINES (Jake, 2026-08-27: "the lines showing where the
  placements are, are very difficult to see. Let's make them thicker and
  figure out what to do about increasing the contrast.")

  WHY THEY WERE INVISIBLE -- measured, not guessed. `stroke-width` here is
  in IMAGE units, and the viewBox (the photo, up to 1600px wide) is
  rendered into a ~558 CSS px frame: a scale of ~0.35. The old 1.6 and 1.4
  therefore reached the screen at 0.56 and 0.49 CSS px -- BELOW ONE PIXEL,
  where the browser antialiases a stroke into a faint grey smear. The
  ruler's 5 rendered ~1.74px and read fine, which is exactly why one thing
  on this stage looked bold while the subject of the page faded out.

  So the numbers below are chosen against the DISPLAY scale, not by eye:
  they clear 1 CSS px at the widest image the page uses, and the spec
  asserts that arithmetic rather than the literals.

  CONTRAST IS A HALO, NOT A DARKER INK. These lines sit over arbitrary
  garment artwork -- white tees, black hoodies, photographs -- so no single
  colour can win everywhere; darkening the blue would simply move the
  failure from light garments to dark ones. Each line is drawn twice
  instead: a translucent WHITE under-stroke, wider, then the coloured
  stroke over it. On a dark garment the blue is separated from its
  background by a light rim; on a light one the rim disappears and the blue
  carries itself. The ink keeps its meaning (blue authored, dashed
  inherited, amber forked -- ADR 0071 decision 5) because only the backing
  changed.
*/
.stage__zone-halo,
.stage__cross-halo {
    fill: none;
    stroke: rgba(255, 255, 255, 0.85);
    /* Inert: it lies directly over the rect the user drags, and a halo
       that swallowed the grab would reintroduce the bug above. */
    pointer-events: none;
}
.stage__zone-halo {
    stroke-width: 6;
}
.stage__cross-halo {
    stroke-width: 5.5;
}
.stage__zone {
    stroke-width: 3.2;
    cursor: move;
}
.stage__frame:focus-visible {
    outline: 2px solid var(--tf-primary, #119ae9);
    outline-offset: 2px;
}
.stage__zone--authored {
    stroke: #119ae9;
    fill: rgba(17, 154, 233, 0.045);
}
.stage__zone--inherited {
    stroke: #4b7fa8;
    stroke-dasharray: 10 8;
    fill: rgba(75, 127, 168, 0.045);
    cursor: default;
}
.stage__zone--different {
    stroke: #c77800;
    fill: rgba(199, 120, 0, 0.045);
}
.stage__cross {
    stroke-width: 2.9;
    /* Was 0.7. The crosshair marks the zone CENTRE -- the thing being
       positioned -- so fading it was working against the halo. */
    opacity: 0.95;
}
.stage__cross.stage__zone--authored {
    stroke: #119ae9;
}
.stage__cross.stage__zone--inherited {
    stroke: #4b7fa8;
}
.stage__cross.stage__zone--different {
    stroke: #c77800;
}
.stage__handle {
    fill: var(--tf-surface);
    stroke: #119ae9;
    stroke-width: 2;
    cursor: nwse-resize;
}
.stage__line {
    stroke: #ef0405;
    stroke-width: 5;
}
.stage__line-tbar {
    stroke: #ef0405;
    stroke-width: 4;
}
/*
  The move-across target. Invisible and FAT: a 5px stroke is not something
  a pointer can reliably catch, and widening the visible line instead would
  make the loudest thing on the stage louder still. `stroke-width` is in
  IMAGE units, so this is ~28 image px rather than 28 CSS px -- at the
  stage's display scale that lands close to a comfortable pointer target
  and shrinks with the frame, which is the behaviour wanted.
*/
.stage__line-body {
    stroke: transparent;
    stroke-width: 28;
    cursor: move;
}
.stage__line-handle {
    fill: var(--tf-surface);
    stroke: #ef0405;
    stroke-width: 4;
    cursor: grab;
}
/*
  The inch field. Still the loudest thing on the stage -- it is the only
  value nothing downstream can check (handoff), hence the 2px red border --
  but no longer the WIDEST. Jake, 2026-08-26: "the place where I type a
  scale obscures a lot of the product on the zone stage."

  Three things shrink it, in the order they mattered:
    * `translateX(0)`, not -50%. Positioned at the line's trailing end, the
      pill now grows outward from that end instead of straddling it, so
      half its width no longer reaches back across the measured span.
    * the derived px readout is focus-only in the template, so the resting
      width is the input plus "in" -- about a third of what it was.
    * a 15px input rather than 19px.

  `white-space: nowrap` stays load-bearing, not cosmetic: without it the
  derived "= 832 px . 37.8 px/in" readout wrapped to four lines inside the
  box and the field swallowed the scale line beneath it. Caught by browser
  verification on 8086; every DOM assertion passed regardless.
*/
.stage__inch-field {
    position: absolute;
    transform: translateX(0);
    display: flex;
    align-items: baseline;
    white-space: nowrap;
    gap: 5px;
    background: var(--tf-surface);
    border: 2px solid #ef0405;
    border-radius: 6px;
    box-shadow: var(--tf-elev-4);
    padding: 3px 7px;
    transition: opacity 150ms cubic-bezier(0.4, 0, 0.2, 1);
}
/*
  Out of the way while a zone is being dragged. `pointer-events: none` is
  the half that matters -- a translucent field the cursor could still
  catch would end a drag on the box underneath it.
*/
.stage__inch-field--muted {
    opacity: 0.25;
    pointer-events: none;
}
.stage__inch-input {
    width: 46px;
    font-family: ui-monospace, "SF Mono", Menlo, Consolas, monospace;
    font-size: 15px;
    border: none;
    outline: none;
    color: var(--tf-fg-1);
    background: transparent;
}
.stage__inch-unit {
    font-size: 11px;
    color: var(--tf-fg-3);
}
.stage__summary {
    margin-top: 8px;
    font-size: 12px;
    color: var(--tf-fg-3);
}
.stage__resized {
    color: #b26a00;
}
.stage__menu {
    border: 1px solid var(--tf-border);
    border-radius: 6px;
    box-shadow: var(--tf-elev-4);
    background: var(--tf-surface);
    margin-bottom: 8px;
    overflow: hidden;
}
.stage__menu-row {
    display: flex;
    justify-content: space-between;
    width: 100%;
    padding: 8px 12px;
    border: none;
    background: transparent;
    font-size: 13px;
    color: var(--tf-fg-2);
    cursor: pointer;
}
.stage__menu-row:hover {
    background: var(--tf-surface-alt);
}
.stage__menu-in {
    font-family: ui-monospace, "SF Mono", Menlo, Consolas, monospace;
    font-size: 11px;
    color: var(--tf-fg-3);
}
.stage__revert-row {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-top: 8px;
}
.stage__revert-note {
    font-size: 12px;
    color: #b26a00;
}
</style>
